import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface CreateUserProps {
  setIsCreatingUser: React.Dispatch<React.SetStateAction<boolean>>;
  isCreatingUser: boolean;
  onUserCreated?: () => void;
}

const CreateUserDialog = ({ setIsCreatingUser, isCreatingUser, onUserCreated }: CreateUserProps) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [team, setTeam] = useState('');
  const [role, setRole] = useState('USER');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setUsername('');
    setPassword('');
    setConfirmPassword('');
    setTeam('');
    setRole('USER');
    setError('');
  };

  const createNewUser = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');

    try {
      if (!username.trim()) {
        throw new Error('Please enter a username');
      }

      if (!password) {
        throw new Error('Please enter a password');
      }

      if (password !== confirmPassword) {
        throw new Error('Passwords do not match. Please try again');
      }

      if (!team.trim()) {
        throw new Error('Please enter a team name');
      }

      setIsLoading(true);

      const response = await fetch(`/api/v1/user`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
          team,
          role,
        }),
      });

      const res = await response.json();

      if (!response.ok) throw new Error(res.message);

      toast.success('New user created successfully');
      setIsCreatingUser(false);
      resetForm();

      if (onUserCreated) {
        onUserCreated();
      }
    } catch (error: any) {
      setError(error.message || `An error occurred while creating the new user.`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog open={isCreatingUser} onOpenChange={setIsCreatingUser}>
      <AlertDialogContent className='sm:max-w-[450px]'>
        <AlertDialogHeader>
          <AlertDialogTitle className='text-xl font-bold'>Create New User</AlertDialogTitle>
          <AlertDialogDescription>Fill in the details below to create a new user.</AlertDialogDescription>
        </AlertDialogHeader>

        <form onSubmit={createNewUser}>
          <div className='space-y-4 py-4'>
            {error && <div className='p-3 text-sm bg-red-100 border border-red-300 text-red-600 rounded-md'>{error}</div>}

            <div className='space-y-2'>
              <Label htmlFor='username'>Username</Label>
              <Input
                id='username'
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder='Enter username'
                disabled={isLoading}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='team'>Team</Label>
              <Input
                id='team'
                value={team}
                onChange={e => setTeam(e.target.value)}
                placeholder='Enter team name'
                disabled={isLoading}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='role'>Role</Label>
              <Select value={role} onValueChange={setRole} disabled={isLoading}>
                <SelectTrigger id='role'>
                  <SelectValue placeholder='Select a role' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='USER'>USER</SelectItem>
                  <SelectItem value='HACKER'>HACKER</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='password'>Password</Label>
              <Input
                id='password'
                type='password'
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder='Enter password'
                disabled={isLoading}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='confirm-password'>Confirm Password</Label>
              <Input
                id='confirm-password'
                type='password'
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder='Confirm password'
                disabled={isLoading}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel
              type='button'
              onClick={() => {
                resetForm();
                setIsCreatingUser(false);
              }}
              disabled={isLoading}
            >
              Cancel
            </AlertDialogCancel>
            <Button type='submit' disabled={isLoading}>
              {isLoading ? (
                <>
                  <svg
                    className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CreateUserDialog;
